"use client"
import Basiclayout from '@/components/atomic/Basiclayout'
import Card from '@/components/atomic/Card'
import Heading from '@/components/atomic/Heading'
import React, { useState, useEffect, useCallback } from 'react'
import DaySelector from './DaySelector'
import { LoadingButton } from '@/components/atomic'
import Image from 'next/image'
import { ICONS } from '@/constants/constants'
import { getDayTrips } from '@/lib/api/trip'
import { useRouter } from 'next/navigation'
interface Trip {
  id: string;
  date: string;
  time: string;
  recurrenceType: 'SINGLE' | 'WEEKLY';
  route: {
    departure: string;
    destination: string;
  };
}

interface RouteData {
  title: string;
  times: {
    time: string;
    label: string;
    duration: string;
  }[];
}

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

const getRouteTitle = (departure: string, destination: string) => {
  const routeMap: { [key: string]: string } = {
    'AER': 'Aero Point',
    'TUK': 'Tuck Inlet'
  };
  return `${routeMap[departure] || departure} → ${routeMap[destination] || destination}`;
};

const processTripsData = (trips: Trip[]): RouteData[] => {
  const routeGroups: { [key: string]: Trip[] } = {};

  trips.forEach(trip => {
    const routeKey = `${trip.route.departure}-${trip.route.destination}`;
    if (!routeGroups[routeKey]) {
      routeGroups[routeKey] = [];
    }
    routeGroups[routeKey].push(trip);
  });

  return Object.entries(routeGroups).map(([routeKey, routeTrips]) => {
    const [departure, destination] = routeKey.split('-');
    return {
      title: getRouteTitle(departure, destination),
      times: routeTrips.map(trip => ({
        time: formatTime(trip.time),
        label: trip.recurrenceType === 'WEEKLY' ? 'Weekly Service' : 'Single Service',
        duration: '40m'
      }))
    };
  });
};

const ScheduleNotes = () => (

  <div className="bg-gray-100 rounded-lg   p-6 w-full flex items-start gap-2">
    <Image src={ICONS.info} alt='info' width={15} height={15} className='mt-1' />
    <div>
      <div className="flex items-center mb-2">

        <span className="font-medium text-gray-800 text-base">Schedule Notes</span>
      </div>
      <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1">
        <li>Schedule subject to change due to weather conditions</li>
        <li>Please arrive 30 minutes before departure</li>
        <li>Vehicle transport must be booked in advance</li>
      </ul>
    </div>
  </div>
);

const Schedule = () => {
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const fetchTrips = useCallback(async (date: string) => {
    if (!date) return;

    setLoading(true);
    setError('');

    try {
      const response = await getDayTrips(date);
      if (response.success) {
        setTrips(response.data.trips);
      } else {
        setError('Failed to fetch trips');
        setTrips([]);
      }
    } catch (err) {
      setError('Error fetching trips');
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDateChange = useCallback((date: string) => {
    setSelectedDate(date);
    fetchTrips(date);
  }, [fetchTrips]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    setSelectedDate(today);
    fetchTrips(today);
  }, [fetchTrips]);

  const routeData = processTripsData(trips);
  const router = useRouter();
  return (
    <Basiclayout className='max-md:pr-0 max-md:pl-0'>
      <Heading className='mt-10 max-md:hidden' heading='FERRY SCHEDULE' subheading='Aero Point ↔ Tuck Inlet' />
         <div className='max-md:block hidden pt-10 pr-5 pl-5'>
        <h1 className='text-2xl font-semibold font-rakesly uppercase'>FERRY SCHEDULE</h1>
        <p className='text-gray-600'>Aero Point ↔ Tuck Inlet</p>
      </div>
      <Card className='mt-10 flex flex-col gap-5 mb-10 max-md:p-0'>
        <DaySelector onDateChange={handleDateChange} />

        {loading && (
          <div className="flex justify-center py-8">
            <div className="text-gray-500">Loading trips...</div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-600">{error}</div>
          </div>
        )}

        {!loading && !error && (
          <>
            {routeData.length > 0 ? (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full text-gray-600 max-md:pr-5 max-md:pl-5'>
                {routeData.map((route) => (
                  <div key={route.title}>
                    <h2 className='text-lg font-normal mb-4'>{route.title}</h2>
                    <div className='flex flex-col gap-4'>
                      {route.times.map((slot, idx) => (
                        <Card key={idx} className='border border-gray-200'>
                          <div>
                            <div className='text-base font-medium text-gray-900'>{slot.time}</div>
                            <div className='text-sm text-gray-500'>{slot.label}</div>
                          </div>
                          <div className='text-sm text-gray-400 font-medium'>{slot.duration}</div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No trips scheduled for this date
              </div>
            )}
          </>
        )}

        <div className='max-md:pr-5 max-md:pl-5 '>
          <ScheduleNotes />
        </div>

        <div className='flex justify-start w-full max-md:pr-5 max-md:pl-5 pb-10'>
          <LoadingButton className='max-md:w-full' onClick={() => router.push('/')}>
            <span className='font-semibold px-10 py-1'>Book a trip</span>
          </LoadingButton>
        </div>
      </Card>
    </Basiclayout>
  )
}

export default Schedule